import React, { useEffect, useState, useMemo, useCallback, useRef, useContext } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import AsyncSelect from "react-select/async";
import { debounce } from "lodash";
import { VehiclesClient } from "../web-api-client.ts";
import { useNavigate } from "react-router-dom";
import useToast from "../hooks/UseToast.js";
import Loader from "./Loader.js";
import UserContext from "../providers/UserContext.js";
import ScannerLauncher from "./ScannerLauncher.js";

const NewVehicle = ({ onComplete }) => {
  const [vehicle, setVehicle] = useState({
    makeId: null,
    modelId: null,
    year: "",
    vin: "",
    stockNumber: "",
    trim: "",
    mileage: null,
    color: "",
  });

  const [selectedMake, setSelectedMake] = useState(null);   // { value, label }
  const [selectedModel, setSelectedModel] = useState(null); // { value, label }

  const navigate = useNavigate();
  const modalRef = useRef(null);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const user = useContext(UserContext);

  const toOption = (x) => ({ value: x.id, label: x.label });

  /** VIN helpers */
  const normalizeVin = (raw) => {
  if (!raw) return "";
  let vin = raw.trim().toUpperCase();

  // If starts with "I" and rest is 17 valid VIN chars, assume prefix
  if (vin.length === 18 && vin.startsWith("I") && /^[A-HJ-NPR-Z0-9]{17}$/.test(vin.slice(1))) {
    vin = vin.slice(1);
  }

  // Standard VIN validation
  if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(vin)) {
    throw new Error("Invalid VIN format");
  }

  return vin;
}
  const isValidVin = (v) => /^[A-HJ-NPR-Z0-9]{17}$/.test(v); // excludes I, O, Q

  /** ------------- MAKES LOADER ------------- **/
  const fetchMakes = useCallback(async (inputValue) => {
    const client = new VehiclesClient();
    const term = inputValue && inputValue.trim() ? inputValue.trim() : undefined;
    const data = await client.getMakes(term);
    return data.map(toOption);
  }, []);

  const debouncedFetchMakes = useMemo(
    () =>
      debounce((input, resolve, reject) => {
        fetchMakes(input).then(resolve).catch(reject);
      }, 300),
    [fetchMakes]
  );

  const loadMakes = useCallback(
    (inputValue) =>
      new Promise((resolve, reject) => {
        debouncedFetchMakes(inputValue, resolve, reject);
      }),
    [debouncedFetchMakes]
  );

  /** ------------- MODELS LOADER (depends on makeId) ------------- **/
  const fetchModels = useCallback(async (makeId, inputValue) => {
    if (!makeId) return [];
    const client = new VehiclesClient();
    const term = inputValue && inputValue.trim() ? inputValue.trim() : undefined;
    // Assuming signature: getModelsByMake(makeId, searchTerm?)
    const data = await client.getModelsByMake(makeId, term);
    return data.map(toOption);
  }, []);

  const debouncedFetchModels = useMemo(
    () =>
      debounce((makeId, input, resolve, reject) => {
        fetchModels(makeId, input).then(resolve).catch(reject);
      }, 300),
    [fetchModels]
  );

  const loadModels = useCallback(
    (inputValue) =>
      new Promise((resolve, reject) => {
        debouncedFetchModels(vehicle.makeId, inputValue, resolve, reject);
      }),
    [debouncedFetchModels, vehicle.makeId]
  );



  /** Cleanup debouncers on unmount */
  useEffect(() => {
    return () => {
      debouncedFetchMakes.cancel();
      debouncedFetchModels.cancel();
    };
  }, [debouncedFetchMakes, debouncedFetchModels]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const updatedValue = type === "checkbox" ? checked : value;
    setVehicle((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const handleSubmit = async (isAddNew) => {
    setLoading(true);
    try {
      const client = new VehiclesClient();
      const serverVehicle = await client.createVehicle({
        ...vehicle,
        dealerId: user.dealerId,
      });

      showToast("Vehicle created successfully!", "success");

      if (isAddNew) {
        // Reset form fields
        setVehicle({
          makeId: null,
          modelId: null,
          year: "",
          vin: "",
          stockNumber: "",
          trim: "",
          mileage: null,
          color: "",
        });
        setSelectedMake(null);
        setSelectedModel(null);

        setTimeout(() => {
          modalRef.current?.scrollTo?.(0, 0);
        }, 100);
      } else {
        if (onComplete) onComplete();
        else {
          navigate(`/vehicles/${serverVehicle.id}`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") event.preventDefault();
  };
  const handleScan = (scannedVin) => {
    const normVin = normalizeVin(scannedVin);
    setVehicle((prev) => ({ ...prev, vin: normVin }));
    showToast(`Scanned VIN: ${normVin}`, "info");
    handleDecodeVin();
  }

  /** --------- NEW: Decode VIN and populate fields --------- */
  const handleDecodeVin = async (vin) => {
    //debugger;
    if(!vin) vin = vehicle.vin;
   // alert(vin)

    vin = normalizeVin(vin);
    if (!isValidVin(vin)) {
      showToast("Please enter a valid 17-character VIN (no I, O, or Q).", "warning");
      return;
    }

    setDecoding(true);
    try {
      const client = new VehiclesClient();

      // Assumes your NSwag client exposes this endpoint as below.
      // If your generated name differs, adjust the method name accordingly.
      const summary = await client.decodeVinSummary(vin, vehicle.year || undefined);

      if (!summary) {
        showToast("No results returned from VIN decode.", "warning");
        return;
      }
      // Update vehicle core fields
      setVehicle((prev) => ({
        ...prev,
        vin,
        year: summary.year ?? prev.year,
        trim: summary.trim ?? prev.trim,
        makeId: summary.makeId ?? prev.makeId,
        modelId: summary.modelId ?? prev.modelId,
      }));

      // Preselect UI dropdowns using returned IDs + labels
      if (summary.makeId) {
        setSelectedMake({ value: summary.makeId, label: summary.make ?? `Make #${summary.makeId}` });
      }
      if (summary.modelId) {
        setSelectedModel({ value: summary.modelId, label: summary.model ?? `Model #${summary.modelId}` });
      }

      showToast("VIN decoded and fields populated.", "success");
    } catch (err) {
      console.error(err);
      showToast("Unable to decode VIN. Please check the VIN and try again.", "danger");
    } finally {
      setDecoding(false);
    }
  };

  return (
    <div>
      <div className="modal-body">
        <div
          className="container"
          ref={modalRef}
          onKeyDown={handleKeyDown}
          style={{ overflowY: "auto", maxHeight: "80vh" }}
        >
          <div className="row">
            <div className="mb-3">
              <label htmlFor="vin" className="form-label">
                Vin
              </label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  id="vin"
                  name="vin"
                  value={vehicle.vin || ""}
                  onChange={(e) =>
                    setVehicle((prev) => ({ ...prev, vin: normalizeVin(e.target.value) }))
                  }
                  required
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleDecodeVin(vehicle.vin)}
                  disabled={decoding}
                  title="Decode with vPIC"
                >
                  {decoding ? "Decoding…" : "Decode VIN"}
                </button>
                            <ScannerLauncher onBarcodeDetected={handleDecodeVin} mode="button" buttonClasses="ms-2" />

              </div>
              <small className="text-muted">Uses NHTSA vPIC to prefill make/model/year/trim.</small>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label">Make</label>
              <AsyncSelect
                classNamePrefix="rs"
                className="react-select-container"
                cacheOptions
                defaultOptions
                loadOptions={loadMakes}
                value={selectedMake}
                onChange={(opt) => {
                  setSelectedMake(opt);
                  setVehicle((prev) => ({
                    ...prev,
                    makeId: opt?.value ?? null,
                    modelId: null,
                  }));
                }}
                placeholder="Search make…"
                isClearable
                noOptionsMessage={() => "Type to search makes"}
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label">Model</label>
              <AsyncSelect
                classNamePrefix="rs"
                className="react-select-container"
                cacheOptions
                defaultOptions={!!vehicle.makeId}
                loadOptions={loadModels}
                value={selectedModel}
                onChange={(opt) => {
                  setSelectedModel(opt);
                  setVehicle((prev) => ({ ...prev, modelId: opt?.value ?? null }));
                }}
                placeholder={vehicle.makeId ? "Search model…" : "Select a make first"}
                isDisabled={!vehicle.makeId}
                isClearable
                noOptionsMessage={() =>
                  vehicle.makeId ? "Type to search models" : "Select a make"
                }
                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
 <label htmlFor="year" className="form-label">
              Year
            </label>
            <input
              type="number"
              className="form-control"
              id="year"
              name="year"
              value={vehicle.year || ""}
              onChange={handleChange}
              required
            />
            </div>
            <div className="col-md-6 mb-3">
 <label htmlFor="trim" className="form-label">
              Trim
            </label>
            <input
              type="text"
              className="form-control"
              id="trim"
              name="trim"
              value={vehicle.trim || ""}
              onChange={handleChange}
            />
            </div>
          </div>

      


          <div className="mb-3">
            <label htmlFor="stockNumber" className="form-label">
              Stock Number
            </label>
            <input
              type="text"
              className="form-control"
              id="stockNumber"
              name="stockNumber"
              value={vehicle.stockNumber || ""}
              onChange={handleChange}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="mileage" className="form-label">
              Mileage
            </label>
            <input
              type="number"
              className="form-control"
              id="mileage"
              name="mileage"
              value={vehicle.mileage || ""}
              onChange={handleChange}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="color" className="form-label">
              Color
            </label>
            <input
              type="text"
              className="form-control"
              id="color"
              name="color"
              value={vehicle.color || ""}
              onChange={handleChange}
            />
          </div>
        

          {loading ? (
            <div className="text-center">
              <Loader />
            </div>
          ) : (
            <div className="text-end">
              <button className="btn btn-primary me-2" onClick={() => handleSubmit(false)}>
                Save
              </button>
              <button className="btn btn-primary" onClick={() => handleSubmit(true)}>
                Save and Add New...
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewVehicle;
